import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Table from '../../components/Table/Table';
import AddButton from '../../components/Button/AddButton';
import { useTranslation } from 'react-i18next';
import { DIGIMAGAZINE_TABLE } from '../../utils/constants';
import { LinkRow, Row } from '../../types';
import AlertModal from '../../components/AlertModal';
import { Box, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';

const ChurchDigiMagazineScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    churchStore: { churches, fetchChurches },
    digiMagazineStore: { itemsArray: digiMagazines, getAllDigiMagazines, deleteDigiMagazine, loading },
  } = useMst();
  const [selectedRow, setSelectedRow] = useState<Row | LinkRow>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [churchFilter, setChurchFilter] = useState<string | 'all'>('all');
  const [fetchedOnce, setFetchedOnce] = useState(false);

  useEffect(() => {
    if (churches.length === 0) fetchChurches('', true);
  }, [fetchChurches, churches.length]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await getAllDigiMagazines();
      } finally {
        if (mounted) setFetchedOnce(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [getAllDigiMagazines]);

  const filtered = useMemo(() => {
    const list = (digiMagazines ?? []).filter((m) => (m as unknown as { churchId?: string | null }).churchId != null);
    const narrowed = list.filter((m) =>
      churchFilter === 'all' ? true : (m as unknown as { churchId?: string | null }).churchId === churchFilter
    );
    const rows: Row[] =
      narrowed.length > 0
        ? narrowed.map((dm) => ({
            id: dm.id,
            name: dm.name,
            value: `${dm.name} - ${dm.publisher}`,
            secondValue: dm.published ? t('published') : t('draft'),
          }))
        : [];
    return rows;
  }, [digiMagazines, churchFilter, t]);

  useEffect(() => {
    if (!selectedRow?.id || modalOpen) return;
    navigate(`/digiMagazines/${selectedRow.id}`);
  }, [selectedRow?.id, modalOpen, navigate]);

  const navigateTo = () => {
    navigate('/digiMagazines/create');
  };

  const deleteMagazine = async () => {
    if (selectedRow?.id) {
      await deleteDigiMagazine(selectedRow.id.toString());
      await getAllDigiMagazines();
      setSelectedRow(undefined);
    }
    setModalOpen(false);
  };

  const toggleDelete = (id: number) => {
    setModalOpen(true);
    setSelectedRow({ id: id });
  };

  const cancel = () => {
    setSelectedRow(undefined);
    setModalOpen(false);
  };

  return (
    <Layout title={t('church.digiMagazines') ?? 'Seurakunnan näköislehdet'}>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="church-filter">{t('church.title') ?? 'Church'}</InputLabel>
            <Select
              labelId="church-filter"
              label={t('church.title') ?? 'Church'}
              value={churchFilter}
              onChange={(e) => setChurchFilter(e.target.value as string)}
            >
              <MenuItem value={'all'}>{t('all') ?? 'All'}</MenuItem>
              {churches.map((c) => (
                <MenuItem key={c.id} value={c.id ?? ''}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <AddButton onClick={navigateTo} text={t('digiMagazines.add_btn')} />
      {!fetchedOnce && loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">{t('loading') ?? 'Loading'}</Typography>
        </Box>
      ) : (
        <Table
          tableType={DIGIMAGAZINE_TABLE}
          data={filtered}
          setSelectedRow={(value) => setSelectedRow({ id: value?.id, value: value?.value })}
          selectedRow={selectedRow}
          onDelete={(id) => toggleDelete(id)}
        />
      )}
      <AlertModal
        open={modalOpen}
        title="digiMagazines.delete"
        onClose={() => setModalOpen(!modalOpen)}
        leftButtonPress={cancel}
        rightButtonPress={deleteMagazine}
      />
    </Layout>
  );
});

export default ChurchDigiMagazineScreen;
